import { useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-toastify/dist/ReactToastify.css";
import "react-loading-skeleton/dist/skeleton.css";
import PageHeading from "../../PageHeading";
import DynamicSelect from "../../DynamicSelect";
import { useTranslation } from "react-i18next";

const AddCategory = ({isSearchable }) => {
  const { t, i18n } = useTranslation([]);
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [categoryData, setCategoryData] = useState({
    translations: {
      en: { name: "", description: "", metaTitle: "", metaDescription: "" },
      bn: { name: "", description: "", metaTitle: "", metaDescription: "" },
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  function convertBengaliToEnglish(str) {
    const mapDigits = {
      "০": "0",
      "১": "1",
      "২": "2",
      "৩": "3",
      "৪": "4",
      "৫": "5",
      "৬": "6",
      "৭": "7",
      "৮": "8",
      "৯": "9",
    };
    return str.replace(/[০-৯]/g, (digit) => mapDigits[digit]);
  }

    // ----------------------------------------------------------------
  //  B) Language Switch (English <-> Bangla)
  // ----------------------------------------------------------------
  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'bn', label: 'Bangla' },
  ];

  const handleLanguageChange = (selectedOption) => {
    i18n.changeLanguage(selectedOption.value);
    setSelectedLanguage(selectedOption.value);
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    const convertedValue = convertBengaliToEnglish(value);

    setCategoryData((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [selectedLanguage]: {
          ...prev.translations[selectedLanguage],
          [name]: convertedValue,
        },
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
  
    try {
      const response = await axios.post("https://api.muktihospital.com/api/category", 
        { translations: categoryData.translations }, // Send JSON directly
        {
          headers: {
            "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
            "Content-Type": "application/json",
          },
        }
      );
  
      if (response.status === 201) {
        toast.success("Category added successfully!");
        setCategoryData({
          translations: {
            en: { name: "", description: "", metaTitle: "", metaDescription: "" },
            bn: { name: "", description: "", metaTitle: "", metaDescription: "" },
          },
        });
      } else {
        toast.error("Failed to add category.");
      }
    } catch (error) {
      console.error("Error adding category:", error);
      toast.error("An error occurred while adding the category.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Manage Category"},
    { label: "Add Category" },
  ];

  return (
    <div>
        <ToastContainer position="top-right" />
        <PageHeading title="Add Category" breadcrumbs={breadcrumbs} />
         {/* Language switcher */}
       <div className="mb-4 flex justify-end">
        <DynamicSelect
            options={languageOptions}
            onChange={handleLanguageChange}
            value={languageOptions.find(option => option.value === i18n.language)}
            isSearchable={isSearchable}
        />
          </div>

        {/* Category Form */}
        <form onSubmit={handleSubmit}>
         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
          <div className="bg-white shadow-sm rounded-lg pb-2">
            <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                  <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                    Basic Information
                  </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5">
                    {/* 1) Category Name */}
                    <div>
              <label className="label">Category Name</label>
              {isSubmitting ? (
                <Skeleton height={35} />
              ) : (
                <input
                  type="text"
                  name="name"
                  placeholder="Enter Category Name"
                  className="input-field"
                  value={categoryData.translations[selectedLanguage].name}
                  onChange={handleChange}
                />
              )}
            </div>

            {/* 2) Category Description */}
            <div>
              <label className="label">Category Description</label>
              {isSubmitting ? (
                <Skeleton height={35} />
              ) : (
                <input
                  type="text"
                  name="description"
                  placeholder="Enter Category Description"
                  className="input-field"
                  value={categoryData.translations[selectedLanguage].description}
                  onChange={handleChange}
                />
              )}
            </div>
              </div>
          </div>
          </div>
          <div>
            {/* SEO Configuration */}
            <div className="bg-white shadow-sm rounded-lg">
             <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                  SEO Configuration
                </h2>
              </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 3) Meta Title */}
            <div>
              <label className="label">Meta Title</label>
              {isSubmitting ? (
                <Skeleton height={35} />
              ) : (
                <input
                  type="text"
                  name="metaTitle"
                  placeholder="Enter Meta Title (SEO)"
                  className="input-field"
                  value={categoryData.translations[selectedLanguage].metaTitle}
                  onChange={handleChange}
                />
              )}
            </div>

                {/* 4) Meta Description */}
            <div>
              <label className="label">Meta Description</label>
              {isSubmitting ? (
                <Skeleton height={50} />
              ) : (
                <textarea
                  name="metaDescription"
                  placeholder="Enter Meta Description (SEO)"
                  className="input-field"
                  value={categoryData.translations[selectedLanguage].metaDescription}
                  onChange={handleChange}
                />
              )}
            </div>
            </div>
             </div>
            {/* Submit Button */}
          <div className="mt-6 flex gap-4 justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </div>
          </div>
          </div>
        </form>
      </div>
  );
};

export default AddCategory;
